"use client";

import React from "react";
import { ArrowRight, BookOpen, Clock, Cpu, FlaskConical, GraduationCap, ShieldCheck, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const servicesData = [{
  id: "service-1",
  title: "Application Consultation",
  description: "Expert guidance to select, evaluate, and configure the right analytical technology for your laboratory workflow.",
  link: "/ApplicationConsultationPage",
  badge: "Pre-Installation"
}, {
  id: "service-2",
  title: "Qualification & Lifecycle Services",
  description: "Structured maintenance, preventative servicing, and multi-year lifecycle management to ensure instrument reliability.",
  link: "/QualificationLifecycleServicesPage",
  badge: "Ongoing Care"
}, {
  id: "service-3",
  title: "Training & Application Support",
  description: "Practical user instruction, method optimization sessions, and direct access to technical specialists.",
  link: "/TrainingApplicationSupportPage",
  badge: "User Enablement"
}];
export default function SupportServices() {
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (index: number) => {
    switch (index) {
      case 0:
        return <FlaskConical className="size-6 text-primary" aria-hidden="true" />;
      case 1:
        return <Wrench className="size-6 text-primary" aria-hidden="true" />;
      case 2:
        return <GraduationCap className="size-6 text-primary" aria-hidden="true" />;
      default:
        return <FlaskConical className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="supportservicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container mx-auto">
        <div className="max-w-3xl mb-12 md:mb-16">
          <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
            Technical Support
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Specialist Support Across Every Stage
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Practical application advice, ongoing instrument maintenance, and team training designed to keep your operations running smoothly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {servicesData.map((service, index) => <motion.div key={service.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card text-card-foreground shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center border border-border">
                      {getServiceIcon(index)}
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground border border-border">
                      {service.badge}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-semibold tracking-tight text-card-foreground">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="pb-6 grow">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-0">
                  <Button asChild variant="outline" className="w-full justify-between group hover:border-primary/50 hover:bg-secondary/60">
                    <Link to={service.link} className="flex items-center justify-between w-full">
                      <span>Explore Service</span>
                      <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}